import React, { useEffect, useState, useCallback } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import {
  Shield,
  Save,
  History,
  Play,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  Activity,
  FileText
} from 'lucide-react';

interface ConfigRow {
  id: number;
  scope: 'global' | 'tenant' | 'module';
  tenant_id: number | null;
  module_name: string | null;
  threshold_high: number;
  threshold_moderate: number;
  weight_self_verification: string | number;
  weight_citation_verification: string | number;
  hard_block_export: boolean;
  soft_warn_export: boolean;
  calibrated_at: string | null;
  calibration_sample_size: number | null;
  notes: string | null;
  updated_at: string;
}

interface EffectiveConfig {
  threshold_high: number;
  threshold_moderate: number;
  weight_self_verification: number | string;
  weight_citation_verification: number | string;
  hard_block_export: boolean;
  soft_warn_export: boolean;
  source: string;
  calibrated_at: string | null;
  calibration_sample_size: number | null;
}

interface CalibrationRunSummary {
  id: number;
  run_id: string;
  label: string | null;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  started_at: string | null;
  finished_at: string | null;
  sample_size: number;
  module_filter: string | null;
  model_filter: string | null;
  brier_score: string | number | null;
  expected_calibration_error: string | number | null;
  mean_confidence: string | number | null;
  mean_actual_accuracy: string | number | null;
  overconfidence_gap: string | number | null;
  recommended_threshold_high: number | null;
  recommended_threshold_moderate: number | null;
  applied_to_config: boolean;
  reliability_diagram?: any[] | null;
  details?: any;
  error?: string | null;
}

interface AuditLogEntry {
  id: number;
  event_type: string;
  user_id: number | null;
  created_at: string;
  old_values: any;
  new_values: any;
}

const VerificationConfig: React.FC = () => {
  const [rows, setRows] = useState<ConfigRow[]>([]);
  const [effective, setEffective] = useState<EffectiveConfig | null>(null);
  const [runs, setRuns] = useState<CalibrationRunSummary[]>([]);
  const [audit, setAudit] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [runningCalibration, setRunningCalibration] = useState(false);
  const [selectedRun, setSelectedRun] = useState<CalibrationRunSummary | null>(null);
  const [formState, setFormState] = useState({
    scope: 'global' as 'global' | 'tenant' | 'module',
    module_name: '',
    threshold_high: 85,
    threshold_moderate: 60,
    weight_self_verification: 0.55,
    weight_citation_verification: 0.45,
    hard_block_export: true,
    soft_warn_export: true,
    notes: ''
  });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [configRes, runsRes, auditRes] = await Promise.all([
        api.get('/api/verification-admin/config'),
        api.get('/api/verification-admin/calibration/runs?limit=25'),
        api.get('/api/verification-admin/audit?limit=50')
      ]);
      setRows(configRes.data.rows || []);
      setEffective(configRes.data.effective || null);
      setRuns(runsRes.data.runs || []);
      setAudit(auditRes.data.logs || []);
      if (configRes.data.effective) {
        setFormState(prev => ({
          ...prev,
          threshold_high: configRes.data.effective.threshold_high,
          threshold_moderate: configRes.data.effective.threshold_moderate,
          weight_self_verification: parseFloat(String(configRes.data.effective.weight_self_verification)),
          weight_citation_verification: parseFloat(String(configRes.data.effective.weight_citation_verification)),
          hard_block_export: configRes.data.effective.hard_block_export,
          soft_warn_export: configRes.data.effective.soft_warn_export
        }));
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to load verification config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleSaveConfig = async () => {
    if (formState.threshold_high <= formState.threshold_moderate) {
      toast.error('High threshold must be greater than moderate threshold');
      return;
    }
    setSavingConfig(true);
    try {
      const payload: any = {
        scope: formState.scope,
        threshold_high: formState.threshold_high,
        threshold_moderate: formState.threshold_moderate,
        weight_self_verification: formState.weight_self_verification,
        weight_citation_verification: formState.weight_citation_verification,
        hard_block_export: formState.hard_block_export,
        soft_warn_export: formState.soft_warn_export,
        notes: formState.notes || null
      };
      if (formState.scope === 'module') {
        payload.module_name = formState.module_name;
      }
      await api.put('/api/verification-admin/config', payload);
      toast.success('Verification configuration saved');
      await loadAll();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to save configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleRunCalibration = async () => {
    setRunningCalibration(true);
    try {
      const res = await api.post('/api/verification-admin/calibration/historical', {
        label: `historical-${new Date().toISOString()}`
      });
      if (res.data.success) {
        toast.success(`Calibration run ${res.data.run.status}`);
      }
      await loadAll();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Calibration failed');
    } finally {
      setRunningCalibration(false);
    }
  };

  const handleApplyCalibration = async (runId: string) => {
    if (!confirm('Apply this calibration run to global thresholds? This will overwrite current values.')) return;
    try {
      await api.post(`/api/verification-admin/calibration/runs/${runId}/apply`, { scope: 'global' });
      toast.success('Calibration applied to global config');
      await loadAll();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to apply calibration');
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Shield className="w-7 h-7 text-purple-600" />
            <h1 className="text-2xl font-bold text-gray-900">Verification Configuration</h1>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Manage confidence thresholds, run calibration, and review the audit trail for verification tuning.
          </p>
        </div>
        <button
          onClick={loadAll}
          className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {effective && (
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900">Effective Configuration</h2>
            <span className={`text-xs px-2 py-1 rounded-full ${
              effective.calibrated_at ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-amber-50 text-amber-700 border border-amber-200'
            }`}>
              {effective.calibrated_at ? 'Empirically calibrated' : 'Using default thresholds'}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-xs text-gray-500">High threshold</div>
              <div className="text-xl font-bold text-green-700">{effective.threshold_high}%</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Moderate threshold</div>
              <div className="text-xl font-bold text-amber-700">{effective.threshold_moderate}%</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Self-verification weight</div>
              <div className="text-xl font-bold text-gray-900">
                {(parseFloat(String(effective.weight_self_verification)) * 100).toFixed(0)}%
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Citation weight</div>
              <div className="text-xl font-bold text-gray-900">
                {(parseFloat(String(effective.weight_citation_verification)) * 100).toFixed(0)}%
              </div>
            </div>
          </div>
          <div className="mt-4 text-xs text-gray-500 flex items-center gap-2 flex-wrap">
            <span>Source: <span className="font-mono">{effective.source}</span></span>
            <span>•</span>
            <span>Hard-block on low confidence: {effective.hard_block_export ? 'ON' : 'OFF'}</span>
            <span>•</span>
            <span>Soft-warn on moderate: {effective.soft_warn_export ? 'ON' : 'OFF'}</span>
            {effective.calibrated_at && (
              <>
                <span>•</span>
                <span>Calibrated {new Date(effective.calibrated_at).toLocaleString()} on {effective.calibration_sample_size || '?'} samples</span>
              </>
            )}
          </div>
          {/* Hard-block rule explanation — blueprint Section 06 */}
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="text-[11px] bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-red-800">
              <span className="font-bold">Hard-block rule:</span> Outputs below the High threshold ({effective.threshold_high ?? 90}%) are hard-blocked from export.
            </div>
            <div className="text-[11px] bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-800">
              <span className="font-bold">Soft-warn rule:</span> Outputs below the Moderate threshold ({effective.threshold_moderate ?? 65}%) generate a soft warning. Calibrate based on empirical ECE gap from Calibration tab.
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Save className="w-5 h-5 text-purple-600" />
            Update Thresholds
          </h2>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Scope</label>
              <select
                value={formState.scope}
                onChange={(e) => setFormState({ ...formState, scope: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="global">Global</option>
                <option value="tenant">Tenant-only</option>
                <option value="module">Per-module</option>
              </select>
            </div>
            {formState.scope === 'module' && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Module name</label>
                <input
                  type="text"
                  value={formState.module_name}
                  onChange={(e) => setFormState({ ...formState, module_name: e.target.value })}
                  placeholder="e.g. sop_generation_enhanced"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">High threshold (%)</label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={formState.threshold_high}
                  onChange={(e) => setFormState({ ...formState, threshold_high: parseInt(e.target.value, 10) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Moderate threshold (%)</label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={formState.threshold_moderate}
                  onChange={(e) => setFormState({ ...formState, threshold_moderate: parseInt(e.target.value, 10) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Self-verification weight</label>
                <input
                  type="number"
                  step="0.05"
                  min={0}
                  max={1}
                  value={formState.weight_self_verification}
                  onChange={(e) => setFormState({ ...formState, weight_self_verification: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Citation weight</label>
                <input
                  type="number"
                  step="0.05"
                  min={0}
                  max={1}
                  value={formState.weight_citation_verification}
                  onChange={(e) => setFormState({ ...formState, weight_citation_verification: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formState.hard_block_export}
                  onChange={(e) => setFormState({ ...formState, hard_block_export: e.target.checked })}
                />
                Hard-block exports on low confidence
              </label>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formState.soft_warn_export}
                  onChange={(e) => setFormState({ ...formState, soft_warn_export: e.target.checked })}
                />
                Soft-warn exports on moderate confidence
              </label>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Notes (audit trail)</label>
              <textarea
                value={formState.notes}
                onChange={(e) => setFormState({ ...formState, notes: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="Reason for this change (visible in audit log)"
              />
            </div>
            <button
              onClick={handleSaveConfig}
              disabled={savingConfig}
              className="w-full bg-purple-600 text-white py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {savingConfig ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {savingConfig ? 'Saving...' : 'Save configuration'}
            </button>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            Calibration
          </h2>
          <p className="text-sm text-gray-600 mb-3">
            Compare historical AI confidence scores against observed accuracy to empirically tune thresholds.
          </p>
          <button
            onClick={handleRunCalibration}
            disabled={runningCalibration}
            className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2 mb-4"
          >
            {runningCalibration ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            {runningCalibration ? 'Running...' : 'Run historical calibration'}
          </button>

          <div className="space-y-2 max-h-80 overflow-y-auto">
            {runs.length === 0 && (
              <div className="text-sm text-gray-500 text-center py-4">No calibration runs yet</div>
            )}
            {runs.map(run => (
              <div
                key={run.id}
                className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 cursor-pointer"
                onClick={() => setSelectedRun(run)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {run.status === 'completed' ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : run.status === 'failed' ? (
                      <AlertTriangle className="w-4 h-4 text-red-600" />
                    ) : (
                      <Activity className="w-4 h-4 text-blue-600 animate-pulse" />
                    )}
                    <span className="text-sm font-medium">{run.label || run.run_id}</span>
                  </div>
                  <span className="text-xs text-gray-500">
                    {run.started_at ? new Date(run.started_at).toLocaleDateString() : '—'}
                  </span>
                </div>
                {run.status === 'completed' && (
                  <div className="mt-2 grid grid-cols-3 gap-2 text-xs text-gray-600">
                    <div>
                      <div className="text-gray-400">Samples</div>
                      <div className="font-semibold">{run.sample_size}</div>
                    </div>
                    <div>
                      <div className="text-gray-400">ECE</div>
                      <div className="font-semibold">{run.expected_calibration_error ? `${run.expected_calibration_error}%` : '—'}</div>
                    </div>
                    <div>
                      <div className="text-gray-400">Gap</div>
                      <div className="font-semibold">{run.overconfidence_gap ? `${run.overconfidence_gap}pp` : '—'}</div>
                    </div>
                  </div>
                )}
                {run.status === 'failed' && run.error && (
                  <div className="mt-1 text-xs text-red-600">{run.error}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-gray-600" />
          Active Configurations
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-600">
              <tr>
                <th className="text-left px-3 py-2">Scope</th>
                <th className="text-left px-3 py-2">Module</th>
                <th className="text-right px-3 py-2">High</th>
                <th className="text-right px-3 py-2">Moderate</th>
                <th className="text-right px-3 py-2">Self / Cite weights</th>
                <th className="text-left px-3 py-2">Calibrated</th>
                <th className="text-left px-3 py-2">Updated</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-4 text-gray-500">No custom configurations. Using static defaults.</td>
                </tr>
              )}
              {rows.map(row => (
                <tr key={row.id} className="border-t border-gray-100">
                  <td className="px-3 py-2 capitalize">{row.scope}</td>
                  <td className="px-3 py-2">{row.module_name || '—'}</td>
                  <td className="px-3 py-2 text-right">{row.threshold_high}%</td>
                  <td className="px-3 py-2 text-right">{row.threshold_moderate}%</td>
                  <td className="px-3 py-2 text-right">
                    {(parseFloat(String(row.weight_self_verification)) * 100).toFixed(0)}% / {(parseFloat(String(row.weight_citation_verification)) * 100).toFixed(0)}%
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {row.calibrated_at ? new Date(row.calibrated_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {new Date(row.updated_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <History className="w-5 h-5 text-gray-600" />
          Configuration Audit Log
        </h2>
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {audit.length === 0 && (
            <div className="text-sm text-gray-500 text-center py-4">No audit entries</div>
          )}
          {audit.map(entry => (
            <div key={entry.id} className="border border-gray-200 rounded-lg p-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs text-purple-700">{entry.event_type}</span>
                <span className="text-xs text-gray-500">{new Date(entry.created_at).toLocaleString()}</span>
              </div>
              {entry.new_values && (
                <pre className="mt-1 text-xs text-gray-600 whitespace-pre-wrap overflow-x-auto">
                  {JSON.stringify(entry.new_values, null, 2).slice(0, 300)}
                </pre>
              )}
            </div>
          ))}
        </div>
      </div>

      {selectedRun && (
        <div className="fixed inset-0 z-50 flex items-center justify-end bg-black/40">
          <div className="w-full max-w-2xl h-full bg-white shadow-2xl overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Calibration Run</h3>
                <p className="text-xs text-gray-500 font-mono">{selectedRun.run_id}</p>
              </div>
              <button
                onClick={() => setSelectedRun(null)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs text-gray-500">Status</div>
                  <div className="font-semibold capitalize">{selectedRun.status}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Samples</div>
                  <div className="font-semibold">{selectedRun.sample_size}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Brier score</div>
                  <div className="font-semibold">{selectedRun.brier_score ?? '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Expected calibration error</div>
                  <div className="font-semibold">{selectedRun.expected_calibration_error ? `${selectedRun.expected_calibration_error}%` : '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Mean confidence</div>
                  <div className="font-semibold">{selectedRun.mean_confidence ?? '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Mean actual accuracy</div>
                  <div className="font-semibold">{selectedRun.mean_actual_accuracy ?? '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Recommended high</div>
                  <div className="font-semibold text-green-700">{selectedRun.recommended_threshold_high ?? '—'}%</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Recommended moderate</div>
                  <div className="font-semibold text-amber-700">{selectedRun.recommended_threshold_moderate ?? '—'}%</div>
                </div>
              </div>

              {selectedRun.reliability_diagram && selectedRun.reliability_diagram.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2 text-sm">Reliability diagram</h4>
                  <div className="space-y-1 text-xs">
                    {selectedRun.reliability_diagram.map((bin: any) => (
                      <div key={bin.bin_index} className="flex items-center gap-2">
                        <span className="w-20 font-mono">{bin.confidence_lower}–{bin.confidence_upper}%</span>
                        <div className="flex-1 bg-gray-100 rounded h-4 relative overflow-hidden">
                          {bin.count > 0 && (
                            <>
                              <div
                                className="absolute top-0 left-0 h-full bg-blue-400 opacity-60"
                                style={{ width: `${Math.min(100, bin.mean_confidence || 0)}%` }}
                              />
                              <div
                                className="absolute top-0 left-0 h-full bg-green-600 opacity-80"
                                style={{ width: `${Math.min(100, bin.mean_actual_accuracy || 0)}%` }}
                              />
                            </>
                          )}
                        </div>
                        <span className="w-16 text-right text-gray-600">n={bin.count}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    <span className="inline-block w-3 h-3 bg-blue-400 opacity-60 rounded mr-1" /> AI confidence
                    <span className="inline-block w-3 h-3 bg-green-600 opacity-80 rounded mr-1 ml-3" /> Actual accuracy
                  </div>
                </div>
              )}

              {selectedRun.details?.recommendation_reason && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
                  {selectedRun.details.recommendation_reason}
                </div>
              )}

              {selectedRun.status === 'completed' && !selectedRun.applied_to_config && (
                <button
                  onClick={() => handleApplyCalibration(selectedRun.run_id)}
                  className="w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
                >
                  <CheckCircle className="w-4 h-4" />
                  Apply to global configuration
                </button>
              )}

              {selectedRun.applied_to_config && (
                <div className="text-sm text-green-700 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Already applied to configuration
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerificationConfig;
